import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { EndpointResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../requestOptions';

/** A sent `filename` names the stored asset and sets its content type from the extension. */
export type AssetSource = Readonly<{ kind: 'file'; file: File; filename: string }> | Readonly<{ kind: 'url'; url: string; filename?: string }>;

/**
 * kitchensink downloads a URL source itself. The result is its in-memory row, whose stored `filename` is empty (a
 * generated column), so callers validate it and re-read the list.
 */
export const createAsset = async (scope: RequestScope, source: AssetSource, options: WriteRequestOptions): Promise<EndpointResponse<unknown>> => {
  try {
    if (source.kind === 'file') {
      const body = new FormData();
      body.append('file', source.file);
      body.append('filename', source.filename);
      const response = await scopedApi(scope).post<EndpointResponse<unknown>>('assets', body, {
        ...options,
        // Let the browser set the multipart boundary; the client's JSON default would turn the FormData into JSON.
        headers: { 'Content-Type': undefined },
      });
      return response.data;
    }
    const body = source.filename === undefined ? { url: source.url } : { url: source.url, filename: source.filename };
    const response = await scopedApi(scope).post<EndpointResponse<unknown>>('assets', body, options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error, { redactRequestData: true });
  }
};
